import React, {
  useCallback,
  useState,
  useMemo,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';

import { ReactComponent as ReloadIcon } from '../../assets/icons/v2/reload.svg';
import { PrimaryButton } from '../Button/ActionButtons';
import useClientDocuments from '../../CoachTools/hooks/useClientDocuments';

import ClientDocumentItem from './components/ClientDocumentItem';
import {
  FILTER_KEY,
  FILTERS,
  TABLE_COLUMNS,
  matchesFilter,
  groupByMonth,
} from './constants';
import texts from './texts.json';
import {
  PanelContainer,
  SearchFilterRow,
  FilterTabs,
  FilterTab,
  TableHeader,
  TableHeaderCell,
  DocumentList,
  MonthGroup,
  MonthHeader,
  MonthLabel,
  MonthCount,
  EmptyState,
  LoadMoreRow,
  PanelLoadingPage,
  PanelSearchBar,
} from './styles';

const ClientDocumentsPanel = ({
  userId,
  channelId,
  onlySaved,
  coachId,
}) => {
  const {
    documents,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    pagesLoaded,
  } = useClientDocuments({
    userId, channelId, onlySaved, coachId,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(FILTER_KEY.ALL);

  const filteredDocuments = useMemo(() => documents.filter((doc) => {
    const matchesSearch = !searchQuery
      || doc.fileName?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch && matchesFilter(doc, activeFilter);
  }), [documents, searchQuery, activeFilter]);

  const grouped = useMemo(() => groupByMonth(filteredDocuments), [filteredDocuments]);

  const isFilterActive = searchQuery || activeFilter !== FILTER_KEY.ALL;

  const loadMoreLabel = useMemo(() => {
    if (isLoadingMore) return texts.loadingMore;
    if (isFilterActive) return format(texts.loadMoreFiltered, { count: pagesLoaded });
    return texts.loadMore;
  }, [isLoadingMore, isFilterActive, pagesLoaded]);

  const handleSearchChange = useCallback((e) => setSearchQuery(e.target.value), []);
  const handleClearSearch = useCallback(() => setSearchQuery(''), []);
  const handleFilterSelect = useCallback((key) => setActiveFilter(key), []);

  if (isLoading) {
    return (
      <PanelContainer>
        <PanelLoadingPage />
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <SearchFilterRow>
        <PanelSearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          clearSearch={handleClearSearch}
          placeholder={texts.searchPlaceholder}
        />
        <FilterTabs>
          {FILTERS.map(({ key, label }) => (
            <FilterTab
              key={key}
              $active={activeFilter === key}
              onClick={() => handleFilterSelect(key)}
            >
              {label}
            </FilterTab>
          ))}
        </FilterTabs>
      </SearchFilterRow>
      <TableHeader>
        {TABLE_COLUMNS.map(({ className, label }) => (
          <TableHeaderCell key={className} className={className}>{label}</TableHeaderCell>
        ))}
      </TableHeader>
      <DocumentList>
        {filteredDocuments.length === 0 ? (
          <EmptyState>
            {hasMore && isFilterActive ? texts.emptyStateHasMore : texts.emptyState}
          </EmptyState>
        ) : (
          grouped.map(([month, docs]) => (
            <MonthGroup key={month}>
              <MonthHeader>
                <MonthLabel>{month}</MonthLabel>
                <MonthCount>
                  {docs.length}
                  {' '}
                  {texts.countItems}
                </MonthCount>
              </MonthHeader>
              {docs.map((doc) => (
                <ClientDocumentItem key={doc.id} document={doc} />
              ))}
            </MonthGroup>
          ))
        )}
        {hasMore && documents.length > 0 && (
          <LoadMoreRow>
            <PrimaryButton
              size="medium"
              icon={<ReloadIcon />}
              onClick={loadMore}
              disabled={isLoadingMore}
              variant="light"
            >
              {loadMoreLabel}
            </PrimaryButton>
          </LoadMoreRow>
        )}
      </DocumentList>
    </PanelContainer>
  );
};

ClientDocumentsPanel.propTypes = {
  coachId: PropTypes.string,
  userId: PropTypes.string,
  channelId: PropTypes.string,
  onlySaved: PropTypes.bool,
};

ClientDocumentsPanel.defaultProps = {
  coachId: null,
  userId: null,
  channelId: null,
  onlySaved: false,
};

export default ClientDocumentsPanel;
